<template>
  <section class="relative text-primary-dark">
    <div class="sm:flex sm:items-center pb-4">
      <div class="sm:flex-auto">
        <h1 class="text-base font-semibold text-primary-dark m-0">Payment Gateway Logs</h1>
        <p class="text-sm text-secondary">A list of all the logs in your account. Click a row for the full transaction timeline.</p>
      </div>
    </div>

    <div class="logs-filters flex flex-wrap items-center gap-2 pb-2">
      <DateInput
        id="startDate"
        :value="search.startDate"
        label="From Date"
        class="w-[190px]"
        @update:value="search.startDate = $event"
      />
      <DateInput
        id="endDate"
        :value="search.endDate"
        label="To Date"
        class="w-[190px]"
        @update:value="search.endDate = $event"
      />
      <BaseInput
        id="reference"
        type="text"
        :value="search.reference"
        label="Reference / Booking"
        placeholder="Search"
        @update:value="search.reference = $event"
      />
      <div class="base-input">
        <label class="label-primary" for="type">Type</label>
        <select
          id="type"
          v-model="search.type"
          class="input-primary"
        >
          <option value="">All</option>
          <option value="init">Setup</option>
          <option value="payment">Payment</option>
          <option value="webhook">Webhook</option>
        </select>
      </div>
      <div class="base-input">
        <label class="label-primary" for="status">Status</label>
        <select
          id="status"
          v-model="search.status"
          class="input-primary"
        >
          <option value="">All</option>
          <option value="created">Created</option>
          <option value="pending">Pending</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="cancelled">Cancelled</option>
          <option value="error">Error</option>
          <option value="other">Other</option>
        </select>
      </div>
      <BaseInput
        id="amount"
        type="number"
        :value="search.amount"
        :min="0"
        label="Amount"
        placeholder="Search"
        @update:value="search.amount = $event"
      />
      <BaseInput
        id="widgetName"
        type="text"
        :value="search.widgetName"
        label="Widget Name"
        placeholder="Search"
        @update:value="search.widgetName = $event"
      />
      <button
        type="button"
        class="btn-primary is-btn-size-default flex items-center gap-2"
        @click="handleSearch"
      >
        Search
      </button>
      <button
        type="button"
        class="btn-secondary is-btn-size-default flex items-center gap-2"
        :disabled="isExporting"
        title="Download the current filtered results as CSV"
        @click="handleExport"
      >
        {{ isExporting ? 'Exporting…' : 'Export CSV' }}
      </button>
      <button
        v-if="hasActiveFilters"
        type="button"
        class="flex items-center gap-1 px-2 py-2 text-sm text-secondary hover:text-primary-main transition-colors"
        title="Reset all filters"
        @click="handleClear"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
        Clear filters
      </button>
    </div>

    <LogsTable
      :logs="logs"
      @payload-modal:open="openPayloadModal"
      @detail:open="openDetail"
    />

    <LogsPagination
      :pagination="pagination"
      @update:page="handlePagination"
    />

    <BaseModal
      v-if="showPayloadModal"
      title="Response Payload"
      @cancel="closePayloadModal"
    >
      <div class="mt-2 bg-gray-100 p-4 rounded-md overflow-auto max-h-96 transition-all">
        <pre class="text-sm">{{ currentPayload }}</pre>
      </div>
    </BaseModal>

    <TransactionDetail
      v-if="showDetailModal"
      :reference="detailReference"
      :rows="detailRows"
      @close="closeDetail"
    />

    <OverlaySpinner v-if="isLoading" />

  </section>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';

import { PaymentGatewayLogsI, PaginationI, TransactionReferenceI } from '@/interfaces/interfaces';

import BaseInput from '@/components/inputs/BaseInput.vue';
import OverlaySpinner from "@/components/loader/OverlaySpinner.vue";
import BaseModal from '@/components/common/BaseModal.vue';
import DateInput from '@/components/PaymentGatewayLogs/DateInput.vue';
import LogsTable from '@/components/PaymentGatewayLogs/LogsTable.vue';
import LogsPagination from '@/components/PaymentGatewayLogs/LogsPagination.vue';
import TransactionDetail from '@/components/PaymentGatewayLogs/TransactionDetail.vue';

const store = useStore();

const isLoading = ref(false);
const isExporting = ref(false);
const logs = ref<PaymentGatewayLogsI[]>([]);
const pagination = ref<PaginationI>({
  lastPage: 0,
  page: 1,
  perPage: 0,
  total: 0,
});
interface SearchFiltersI {
  startDate: Date | null,
  endDate: Date | null,
  reference: string | null,
  type: string,
  status: string,
  amount: number | null,
  widgetName: string | null,
}

const emptyFilters = (): SearchFiltersI => ({
  startDate: null,
  endDate: null,
  reference: null,
  type: '',
  status: '',
  amount: null,
  widgetName: null,
});

const search = ref<SearchFiltersI>(emptyFilters());
const showPayloadModal = ref(false);
const currentPayload = ref('');
const showDetailModal = ref(false);
const detailReference = ref<TransactionReferenceI | null>(null);
const detailRows = ref<PaymentGatewayLogsI[]>([]);

onMounted(() => {
  getLogs();
})

// Local calendar date as Y-m-d — serialising the Date object itself would
// shift it through UTC and filter by the wrong day.
const toDateParam = (date: Date | null): string | null => {
  if (!date) {
    return null;
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const searchParams = () => ({
  startDate: toDateParam(search.value.startDate),
  endDate: toDateParam(search.value.endDate),
  reference: search.value.reference,
  type: search.value.type,
  status: search.value.status,
  amount: search.value.amount,
  widgetName: search.value.widgetName,
});

const openPayloadModal = (payload: string) => {
  try {
    const parsedPayload = JSON.parse(payload);
    currentPayload.value = JSON.stringify(parsedPayload, null, 2);
    showPayloadModal.value = true;
  } catch (error) {
    console.error('Error parsing payload:', error);
    // Fallback to raw string if JSON parsing fails
    currentPayload.value = payload;
    showPayloadModal.value = true;
  }
};

const closePayloadModal = () => {
  showPayloadModal.value = false;
  currentPayload.value = '';
};

// Show the clicked row alone when there is nothing to correlate on (or the
// timeline fetch fails) — a click must never silently do nothing.
const openSingleRowDetail = (log: PaymentGatewayLogsI) => {
  detailReference.value = null;
  detailRows.value = [log];
  showDetailModal.value = true;
};

const openDetail = (log: PaymentGatewayLogsI) => {
  // Rows like credential checks have no gateway transaction id or booking
  // reference — there is no wider lifecycle to fetch.
  if (!log.transactionReferenceId && !log.identifier) {
    openSingleRowDetail(log);
    return;
  }

  isLoading.value = true;

  store
    .dispatch('getPaymentGatewayLogTransaction', {
      transactionId: log.transactionReferenceId,
      reference: log.identifier,
    })
    .then((data) => {
      detailReference.value = data?.reference || null;
      detailRows.value = data?.data || [];
      showDetailModal.value = true;
      isLoading.value = false;
    })
    .catch((error) => {
      console.error(error);
      openSingleRowDetail(log);
      isLoading.value = false;
    });
};

const closeDetail = () => {
  showDetailModal.value = false;
  detailReference.value = null;
  detailRows.value = [];
};

const getLogs = async (isPagination = false) => {
  isLoading.value = true;

  store
    .dispatch('getPaymentGatewayLogs', {
      page: isPagination ? pagination.value.page : null,
      ...searchParams(),
    })
    .then(() => {
      logs.value = store.state.paymentGatewayLogs?.data || [];
      pagination.value = store.state.paymentGatewayLogs?.pagination || {};
      isLoading.value = false;
    })
    .catch((error) => {
      console.error(error);
      isLoading.value = false;
    })
}

const hasActiveFilters = computed(() =>
  Object.values(search.value).some(value => value !== null && value !== '')
);

const handleSearch = () => {
  getLogs();
}

const handleClear = () => {
  search.value = emptyFilters();
  getLogs();
}

const handleExport = () => {
  isExporting.value = true;

  store
    .dispatch('exportPaymentGatewayLogs', searchParams())
    .then(() => {
      isExporting.value = false;
    })
    .catch((error) => {
      console.error(error);
      isExporting.value = false;
    });
}

const handlePagination = (page: number) => {
  pagination.value.page = page;
  getLogs(true);
}
</script>

<style>
/* Denser controls on this page only — the shared classes stay untouched. */
.logs-filters .input-primary {
  @apply py-2 text-sm;
}
.logs-filters .is-btn-size-default {
  @apply text-sm px-3.5 py-2;
}
.logs-filters .base-input .label-primary {
  @apply left-4;
}
</style>
